import { Platform } from 'react-native';
import { LOGO_B64 } from './logoB64';

const W = 1080;
const H = 1080;

const C = {
  bg: '#0A0A0A',
  surface: '#141414',
  surfaceHi: '#1C1C1C',
  gold: '#FFD700',
  goldDim: 'rgba(255,215,0,0.15)',
  green: '#22C55E',
  white: '#FFFFFF',
  muted: '#888888',
  border: 'rgba(255,215,0,0.25)',
};

function ensureRoundRect(): void {
  if (typeof CanvasRenderingContext2D === 'undefined') return;
  const proto = CanvasRenderingContext2D.prototype as unknown as Record<string, unknown>;
  if (proto['roundRect']) return;
  proto['roundRect'] = function (
    this: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number,
  ) {
    const rad = Math.min(r, w / 2, h / 2);
    this.moveTo(x + rad, y);
    this.arcTo(x + w, y, x + w, y + h, rad);
    this.arcTo(x + w, y + h, x, y + h, rad);
    this.arcTo(x, y + h, x, y, rad);
    this.arcTo(x, y, x + w, y, rad);
    this.closePath();
  };
}

function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  (ctx as unknown as Record<string, (...a: number[]) => void>)['roundRect'].call(ctx, x, y, w, h, r);
}

async function loadImg(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new (window as unknown as { Image: new () => HTMLImageElement }).Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

async function drawCard(ctx: CanvasRenderingContext2D, iNeed: string[]): Promise<void> {
  // ── Fondo negro con glow dorado arriba ──────────────────────────────────────
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, H);

  const topGlow = ctx.createRadialGradient(W / 2, 0, 0, W / 2, 0, 520);
  topGlow.addColorStop(0, 'rgba(255,215,0,0.22)');
  topGlow.addColorStop(1, 'rgba(255,215,0,0)');
  ctx.fillStyle = topGlow;
  ctx.fillRect(0, 0, W, 520);

  // Puntos decorativos sutiles
  ctx.fillStyle = 'rgba(255,255,255,0.022)';
  for (let i = 0; i < 90; i++) {
    const x = (i * 173 + 53) % W;
    const y = (i * 251 + 97) % H;
    ctx.beginPath();
    ctx.arc(x, y, 1.4, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── Header: logo + wordmark ──────────────────────────────────────────────────
  const logoSize = 80;
  const headerY = 56;
  const logo = await loadImg(LOGO_B64);
  if (logo) {
    ctx.drawImage(logo, 60, headerY, logoSize, logoSize);
  }

  const wordX = logo ? 60 + logoSize + 20 : 60;
  ctx.textAlign = 'left';
  ctx.font = '900 46px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = C.gold;
  ctx.fillText('CAMBIA', wordX, headerY + 52);
  const w1 = ctx.measureText('CAMBIA').width;
  ctx.fillStyle = C.white;
  ctx.fillText('FIGURITAS', wordX + w1 + 6, headerY + 52);

  ctx.font = '500 24px system-ui, sans-serif';
  ctx.fillStyle = C.muted;
  ctx.fillText('Mundial 2026  •  cambiafiguritas.online', wordX, headerY + 84);

  // Separador dorado
  const sepY = headerY + 110;
  const sepGrad = ctx.createLinearGradient(60, sepY, W - 60, sepY);
  sepGrad.addColorStop(0, 'rgba(255,215,0,0)');
  sepGrad.addColorStop(0.2, 'rgba(255,215,0,0.4)');
  sepGrad.addColorStop(0.8, 'rgba(255,215,0,0.4)');
  sepGrad.addColorStop(1, 'rgba(255,215,0,0)');
  ctx.strokeStyle = sepGrad;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(60, sepY);
  ctx.lineTo(W - 60, sepY);
  ctx.stroke();

  // ── Hero: texto principal ────────────────────────────────────────────────────
  const heroY = sepY + 48;

  ctx.textAlign = 'left';
  ctx.font = '700 26px system-ui, sans-serif';
  ctx.fillStyle = C.gold;
  ctx.fillText('MIS REPETIDAS', 60, heroY);

  const count = iNeed.length;
  const countTxt = String(count);

  const numFont = '900 180px system-ui, sans-serif';
  ctx.font = numFont;
  ctx.fillStyle = count > 0 ? C.gold : C.muted;

  if (count > 0) {
    ctx.shadowColor = C.gold;
    ctx.shadowBlur = 48;
  }
  ctx.fillText(countTxt, 60, heroY + 190);
  ctx.shadowBlur = 0;

  // Medir el ancho real del número con la fuente usada
  const countW = ctx.measureText(countTxt).width;
  const unitX = 60 + countW + 24;

  ctx.font = '600 44px system-ui, sans-serif';
  ctx.fillStyle = C.white;
  ctx.fillText(count === 1 ? 'figurita repetida' : 'figuritas repetidas', unitX, heroY + 150);
  ctx.font = '500 30px system-ui, sans-serif';
  ctx.fillStyle = C.muted;
  ctx.fillText('del Mundial 2026', unitX, heroY + 192);

  // ── Chips de figuritas ───────────────────────────────────────────────────────
  const chipsStartY = heroY + 220;

  if (count === 0) {
    ctx.textAlign = 'center';
    ctx.font = '600 36px system-ui, sans-serif';
    ctx.fillStyle = C.muted;
    ctx.fillText('No me sirve ninguna de esa lista.', W / 2, chipsStartY + 80);
    ctx.font = '500 28px system-ui, sans-serif';
    ctx.fillText('Probá con otra lista.', W / 2, chipsStartY + 130);
    ctx.textAlign = 'left';
  } else {
    // Superficie para los chips
    ctx.fillStyle = C.surface;
    rr(ctx, 44, chipsStartY - 16, W - 88, 340, 24);
    ctx.fill();
    ctx.strokeStyle = C.border;
    ctx.lineWidth = 1.5;
    rr(ctx, 44, chipsStartY - 16, W - 88, 340, 24);
    ctx.stroke();

    ctx.font = '700 26px system-ui, sans-serif';
    const chipH = 52;
    const chipGap = 10;
    const rowGap = 12;
    const chipPx = 68;
    const maxChipW = W - chipPx * 2;
    const maxRows = 4;

    let chipX = chipPx;
    let chipY = chipsStartY + 14;
    let row = 0;
    let drawn = 0;

    for (let i = 0; i < iNeed.length; i++) {
      const code = iNeed[i];
      const tw = ctx.measureText(code).width;
      const cw = tw + 28;

      const isLastRow = row === maxRows - 1;
      const remaining = iNeed.length - drawn;
      const needsBadge = isLastRow && remaining > 1;
      const badgeTxt = `+${remaining - 1}`;
      const badgeW = needsBadge ? ctx.measureText(badgeTxt).width + 24 : 0;

      if (chipX + cw + (needsBadge ? chipGap + badgeW : 0) > chipPx + maxChipW) {
        if (row >= maxRows - 1) {
          // Dibujar badge +N
          const more = `+${iNeed.length - drawn}`;
          const mw = ctx.measureText(more).width + 24;
          ctx.fillStyle = 'rgba(255,255,255,0.08)';
          rr(ctx, chipX, chipY, mw, chipH, 12);
          ctx.fill();
          ctx.fillStyle = C.muted;
          ctx.textAlign = 'center';
          ctx.font = '600 24px system-ui, sans-serif';
          ctx.fillText(more, chipX + mw / 2, chipY + 34);
          ctx.textAlign = 'left';
          ctx.font = '700 26px system-ui, sans-serif';
          break;
        }
        row++;
        chipX = chipPx;
        chipY += chipH + rowGap;
      }

      // Chip verde
      ctx.fillStyle = 'rgba(34,197,94,0.15)';
      rr(ctx, chipX, chipY, cw, chipH, 12);
      ctx.fill();
      ctx.strokeStyle = 'rgba(34,197,94,0.5)';
      ctx.lineWidth = 1;
      rr(ctx, chipX, chipY, cw, chipH, 12);
      ctx.stroke();
      ctx.fillStyle = C.green;
      ctx.textAlign = 'center';
      ctx.fillText(code, chipX + cw / 2, chipY + 34);
      ctx.textAlign = 'left';

      chipX += cw + chipGap;
      drawn++;
    }
  }

  // ── Footer ───────────────────────────────────────────────────────────────────
  const footerY = H - 180;

  const footerGrad = ctx.createLinearGradient(44, footerY, W - 44, footerY + 140);
  footerGrad.addColorStop(0, '#181808');
  footerGrad.addColorStop(1, '#101008');
  ctx.fillStyle = footerGrad;
  rr(ctx, 44, footerY, W - 88, 140, 20);
  ctx.fill();
  ctx.strokeStyle = C.border;
  ctx.lineWidth = 1.5;
  rr(ctx, 44, footerY, W - 88, 140, 20);
  ctx.stroke();

  ctx.textAlign = 'left';
  ctx.font = '800 34px system-ui, sans-serif';
  ctx.fillStyle = C.white;
  ctx.fillText('¿Qué figuritas te sirven de mi lista?', 80, footerY + 52);
  ctx.font = '500 26px system-ui, sans-serif';
  ctx.fillStyle = C.gold;
  ctx.fillText('Descubrilo gratis en cambiafiguritas.online', 80, footerY + 90);
  ctx.font = '500 22px system-ui, sans-serif';
  ctx.fillStyle = C.muted;
  ctx.fillText('Intercambiá figuritas del Mundial 2026', 80, footerY + 124);
}

async function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png', 0.95));
}

export type TradeCardResult = 'shared' | 'downloaded' | 'text_shared' | 'text_copied' | 'cancelled' | 'error';

export async function shareTradeCard(iNeed: string[], shareText: string): Promise<TradeCardResult> {
  if (Platform.OS !== 'web') {
    try {
      const { Linking } = await import('react-native');
      const url = `whatsapp://send?text=${encodeURIComponent(shareText)}`;
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
        return 'text_shared';
      }
      // Fallback: share sheet genérico si WhatsApp no está instalado
      const { Share } = await import('react-native');
      const result = await Share.share({ message: shareText });
      if (result.action === Share.dismissedAction) return 'cancelled';
      return 'text_shared';
    } catch {
      return 'error';
    }
  }

  if (typeof document === 'undefined') return 'error';

  ensureRoundRect();
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return 'error';

  try {
    await drawCard(ctx, iNeed);
  } catch {
    return 'error';
  }

  const blob = await canvasToBlob(canvas);
  if (!blob) return 'error';

  const fileName = 'cambiafiguritas-intercambio.png';
  const nav = navigator as unknown as Record<string, unknown>;

  // Web con navigator.share + files (mobile Chrome/Safari)
  if (typeof nav['canShare'] === 'function' && typeof nav['share'] === 'function') {
    try {
      const file = new File([blob], fileName, { type: 'image/png' });
      if ((nav['canShare'] as (d: unknown) => boolean)({ files: [file] })) {
        await (nav['share'] as (d: unknown) => Promise<void>)({
          files: [file],
          title: '¿Qué figuritas de mi lista te sirven?',
          text: shareText,
        });
        return 'shared';
      }
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') return 'cancelled';
    }
  }

  // Fallback desktop: descargar imagen + abrir WhatsApp web con el texto
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    // Abrir WhatsApp web con el texto (nueva pestaña)
    const waUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');

    return 'downloaded';
  } catch {
    return 'error';
  }
}
